import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getDirections } from '@/lib/mapbox';

/** Start point moved further than this since the last calculation → offer Recalculate. */
export const RECALC_DRIFT_M = 500;

/** Rough metres between two {lng,lat} points: 1 degree ≈ 111320 m. */
export function driftMeters(a, b) {
  const dlng = b.lng - a.lng;
  const dlat = b.lat - a.lat;
  return Math.sqrt(
    (dlat * 111320) ** 2 +
    (dlng * 111320 * Math.cos(b.lat * Math.PI / 180)) ** 2,
  );
}

/**
 * Route calculator hook — manages Mapbox routing state.
 * Reusable by Phase 22 Dispatch Board.
 */
export default function useRouteCalculator() {
  const positions = useSelector((state) => state.session.positions);
  const staffByUserId = useSelector((state) => state.staff?.byUserId);

  const [from, setFromState] = useState(null);       // { lng, lat, label, type: 'vehicle'|'pin' }
  const [to, setToState] = useState(null);            // { lng, lat, label, type: 'poi'|'pin' }
  const [routes, setRoutes] = useState([]);           // [{ distance, duration, geometry }]
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSelectingPoint, setIsSelectingPoint] = useState(null); // 'from' | 'to' | null
  const [error, setError] = useState(null);
  const [needsRecalculate, setNeedsRecalculate] = useState(false);

  // Track the vehicle ID / staff user ID separately so we can watch position updates
  const vehicleIdRef = useRef(null);
  const staffUserIdRef = useRef(null);
  const lastCalcPosition = useRef(null); // { lng, lat } at last calculation
  const debounceTimer = useRef(null);

  // Set "from" point — can be from vehicle, map click, or POI
  const setFrom = useCallback((point) => {
    setFromState(point);
    vehicleIdRef.current = point?.type === 'vehicle' ? point.deviceId : null;
    staffUserIdRef.current = point?.type === 'staff' ? point.userId : null;
    setError(null);
  }, []);

  // Set "to" point — can be from map click or POI
  const setTo = useCallback((point) => {
    setToState(point);
    setError(null);
  }, []);

  // Enter point-selection mode (map click)
  const startSelectingPoint = useCallback((which) => {
    setIsSelectingPoint(which); // 'from' or 'to'
  }, []);

  const cancelSelectingPoint = useCallback(() => {
    setIsSelectingPoint(null);
  }, []);

  // Calculate route via Mapbox. Returns true on success, false on error.
  const calculateRoute = useCallback(async (fromOverride, toOverride) => {
    const f = fromOverride || from;
    const t = toOverride || to;
    if (!f || !t) return false;

    setIsCalculating(true);
    setError(null);

    try {
      const result = await getDirections(f, t, { alternatives: true });
      setRoutes(result.routes);
      setSelectedRouteIndex(0);
      lastCalcPosition.current = { lng: f.lng, lat: f.lat };
      setNeedsRecalculate(false);
      return true;
    } catch (err) {
      setError(err.message);
      setRoutes([]);
      return false;
    } finally {
      setIsCalculating(false);
    }
  }, [from, to]);

  // Debounced calculate (for pin drag)
  const calculateRouteDebounced = useCallback((f, t) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      calculateRoute(f, t);
    }, 600);
  }, [calculateRoute]);

  // Select an alternative route
  const selectRoute = useCallback((index) => {
    if (index >= 0 && index < routes.length) {
      setSelectedRouteIndex(index);
    }
  }, [routes.length]);

  // Clear everything
  const clearRoute = useCallback(() => {
    setFromState(null);
    setToState(null);
    setRoutes([]);
    setSelectedRouteIndex(0);
    setIsCalculating(false);
    setIsSelectingPoint(null);
    setError(null);
    setNeedsRecalculate(false);
    vehicleIdRef.current = null;
    staffUserIdRef.current = null;
    lastCalcPosition.current = null;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  // Watch the live start point — a vehicle's latest fix or a staff phone's
  // lastFix — update "from" silently and flag recalculate if it drifted >500m.
  useEffect(() => {
    if (!from) return;
    let newLng;
    let newLat;
    if (from.type === 'vehicle' && vehicleIdRef.current) {
      const pos = positions[vehicleIdRef.current];
      if (!pos || (pos.latitude === 0 && pos.longitude === 0)) return;
      newLng = pos.longitude;
      newLat = pos.latitude;
    } else if (from.type === 'staff' && staffUserIdRef.current) {
      const fix = staffByUserId?.[staffUserIdRef.current]?.lastFix;
      if (!fix || (fix.latitude === 0 && fix.longitude === 0)) return;
      newLng = fix.longitude;
      newLat = fix.latitude;
    } else {
      return;
    }

    setFromState((prev) => {
      if (!prev || prev.type !== from.type) return prev;
      if (prev.lng === newLng && prev.lat === newLat) return prev;
      return { ...prev, lng: newLng, lat: newLat };
    });

    // Check drift from last calculated position
    if (lastCalcPosition.current && routes.length > 0) {
      if (driftMeters(lastCalcPosition.current, { lng: newLng, lat: newLat }) > RECALC_DRIFT_M) {
        setNeedsRecalculate(true);
      }
    }
  }, [positions, staffByUserId, from, routes.length]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Build GeoJSON FeatureCollection for all routes
  const routeGeoJSON = routes.length > 0 ? {
    type: 'FeatureCollection',
    features: routes.map((r, i) => ({
      type: 'Feature',
      geometry: r.geometry,
      properties: {
        index: i,
        selected: i === selectedRouteIndex,
        distance: r.distance,
        duration: r.duration,
      },
    })),
  } : null;

  // Selected route shorthand
  const selectedRoute = routes[selectedRouteIndex] || null;

  return {
    // State
    from,
    to,
    routes,
    selectedRouteIndex,
    selectedRoute,
    isCalculating,
    isSelectingPoint,
    error,
    needsRecalculate,
    routeGeoJSON,

    // Actions
    setFrom,
    setTo,
    startSelectingPoint,
    cancelSelectingPoint,
    calculateRoute,
    calculateRouteDebounced,
    selectRoute,
    clearRoute,
  };
}
